import axios from "axios";
import Cookies from "js-cookie";

// Function to delete a video by video_id
export const deleteVideoById = async (videoId) => {
  try {
    const response = await apiClient.delete(
      buildApiUrl(`/video/${videoId}`, API_VERSIONS.V2),
    );
    return response.data;
  } catch (error) {
    console.error(`Error deleting video with ID ${videoId}:`, error);
    throw error;
  }
};

// Utility to download an image directly from the API as a file
export const downloadPatientImage = async (patientId, picPath) => {
  try {
    const url = buildApiUrl(`/patient/${patientId}/pic`, API_VERSIONS.V2);
    // Use axios directly to get the blob and headers
    const response = await apiClient.get(url, {
      params: { pic_path: picPath },
      responseType: "blob",
    });

    // Try to extract filename from Content-Disposition header
    let filename = "download.jpg";
    const disposition = response.headers["content-disposition"];
    if (disposition) {
      const match = disposition.match(/filename="?([^";]+)"?/);
      if (match && match[1]) {
        filename = decodeURIComponent(match[1]);
      }
    }

    // Create a blob URL and trigger download
    const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = urlBlob;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(urlBlob);
  } catch (error) {
    console.error("Error downloading patient image:", error);
    throw error;
  }
};

// Function to get HTTP URL for a patient picture by pic_path
export const getPatientPicHttpUrl = async (patientId, picPath) => {
  try {
    const url = buildApiUrl(`/patient/${patientId}/pic`, API_VERSIONS.V2);
    const response = await apiClient.get(url, {
      params: { pic_path: picPath },
    });
    // Expecting response.data.url or similar
    return response.data.url;
  } catch (error) {
    console.error("Error fetching patient picture HTTP URL:", error);
    throw error;
  }
};

export const apiClient = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}`,
  headers: {
    "Content-Type": "application/json",
  },
});

const API_VERSIONS = {
  V1: "v1",
  V2: "v2",
};

const buildApiUrl = (endpoint, version = API_VERSIONS.V1) => {
  return `/api/${version}${endpoint}`;
};

// Add a request interceptor to include the access token in the Authorization header
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = Cookies.get("access_token"); // Get the access token from cookies
    const refreshToken = Cookies.get("refresh_token"); // Get the refresh token from cookies
    if (accessToken) {
      config.headers.Authorization = accessToken; // Set the Authorization header
    } else if (refreshToken) {
      config.headers.Authorization = refreshToken;
      const accessToken = refreshAccessToken();
      config.headers.Authorization = accessToken; // Set the Authorization header
    }

    return config;
  },
  (error) => {
    throw error; // Re-throw the error so it reaches the interceptor
  },
);

export const fetchVideoList = async () => {
  try {
    const response = await apiClient.get(
      buildApiUrl("/video", API_VERSIONS.V2),
    );
    const normalizeVideos = (videos = []) =>
      videos.map((video) => ({
        ...video,
        allow_for_retraining: Boolean(video.allow_for_retraining),
      }));

    return {
      ...response.data,
      labeled_videos: normalizeVideos(response.data?.labeled_videos),
      non_labeled_videos: normalizeVideos(response.data?.non_labeled_videos),
    };
  } catch (error) {
    console.error("Error fetching video data:", error);
    throw error;
  }
};

// Function to fetch detection results by video ID and detection ID
export const fetchDetectionResultById = async (videoId, detectionId) => {
  try {
    const response = await apiClient.get(
      buildApiUrl(
        `/video/${videoId}/detection/${detectionId}`,
        API_VERSIONS.V2,
      ),
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching detection results for video ID ${videoId} and detection ID ${detectionId}:`,
      error,
    );
    throw error;
  }
};

// Function to fetch detection list by video ID
export const fetchDetectionList = async (videoId) => {
  try {
    const response = await apiClient.get(
      buildApiUrl(`/video/${videoId}/detection`, API_VERSIONS.V2),
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching detection list:", error);
    throw error;
  }
};

// Function to upload updated data
export const uploadUpdatedData = async (videoId, combinedData) => {
  try {
    await apiClient.post(
      buildApiUrl(`/video/${videoId}/detection`, API_VERSIONS.V2),
      {
        predict: combinedData,
      },
    );
  } catch (error) {
    console.error("Error uploading updated data:", error);
    throw error;
  }
};

export const fetchVideoById = async (videoId) => {
  try {
    const playback = await getVideoPlaybackUrl(videoId);

    return {
      videoUrl: playback.url,
      expiresIn: playback.expires_in,
      expiresAt: playback.expires_at,
    };
  } catch (error) {
    console.error(`Error fetching playback URL for video ID ${videoId}:`, error);
    throw error;
  }
};

export const getVideoUrl = (videoId) => {
  return `${process.env.REACT_APP_API_BASE_URL}${buildApiUrl(`/video/${videoId}`, API_VERSIONS.V2)}`;
};

export const getVideoPlaybackUrl = async (videoId) => {
  try {
    const response = await apiClient.get(
      buildApiUrl(`/video/${videoId}/playback-url`, API_VERSIONS.V2),
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching playback URL for video ID ${videoId}:`, error);
    throw error;
  }
};

export const loginUser = async (username, password) => {
  try {
    console.log("API Base URL:", apiClient.defaults.baseURL);

    // Create a FormData object and append the username and password
    const formData = new FormData();
    formData.append("username", username);
    formData.append("password", password);

    const response = await apiClient.post(
      buildApiUrl("/user/login"),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error("Error logging in:", error);
    throw error;
  }
};

// Function to refresh the access token
export const refreshAccessToken = async () => {
  try {
    const refreshToken = Cookies.get("refresh_token");
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }
    const response = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}${buildApiUrl("/user/refresh")}`,
      null,
      {
        headers: {
          Authorization: refreshToken,
        },
      },
    );

    const { access_token, refresh_token, token_type } = response.data;
    Cookies.set("access_token", `${token_type} ${access_token}`, {
      expires: 30 / (24 * 60),
      path: "/",
    }); // Update access token in cookies
    Cookies.set("refresh_token", `${token_type} ${refresh_token}`, {
      expires: 120 / (24 * 60),
    }); // Update refresh token in cookies
    return `${token_type} ${access_token}`;
  } catch (error) {
    console.error("Error refreshing access token:", error);
    Cookies.remove("access_token");
    Cookies.remove("refresh_token");
    throw error; // Re-throw the error to handle it in the calling function
  }
};

// Function to log out the user
export const logoutUser = async () => {
  try {
    await apiClient.post(buildApiUrl("/user/logout")); // Call the logout API
    Cookies.remove("access_token"); // Remove access token from cookies
    Cookies.remove("refresh_token"); // Remove refresh token from cookies
    console.log("User logged out successfully");
  } catch (error) {
    console.error("Error logging out:", error);
    throw error; // Re-throw the error to handle it in the calling function
  }
};

// Function to register a new account
export const registerUser = async (username, email, fullName, password) => {
  try {
    const response = await apiClient.post(buildApiUrl("/user"), {
      username,
      email,
      full_name: fullName,
      password,
    });
    return response.data; // Return the response data (e.g., success message or user info)
  } catch (error) {
    console.error("Error registering user:", error);
    throw error; // Re-throw the error to handle it in the calling function
  }
};

// Function to create a user with role (for admin, RA, doctor, labeler, institution_owner, parents)
export const createUser = async (userData) => {
  try {
    const requestBody = {
      email: userData.email,
      full_name: userData.full_name,
      platform_role: userData.platform_role,
      profile_permissions: userData.profile_permissions || [],
      username: userData.email, // Use email as username
      metadata: userData.metadata || {},
      password: userData.password,
      assigned_to: userData.assigned_to || null,
    };

    const response = await apiClient.post(buildApiUrl("/user"), requestBody);

    return response.data;
  } catch (error) {
    console.error("Error creating user:", error);
    throw error;
  }
};

// Function to fetch users by role
export const fetchUsersByRole = async (role) => {
  try {
    const response = await apiClient.get(buildApiUrl("/user"), {
      params: {
        platform_role: role,
      },
    });
    return response.data.users;
  } catch (error) {
    console.error(`Error fetching users with role ${role}:`, error);
    throw error;
  }
};

// Function to fetch user details by username
export const fetchUserByUsername = async (username) => {
  try {
    const response = await apiClient.get(buildApiUrl(`/user/${username}`));
    return response.data;
  } catch (error) {
    console.error(`Error fetching user with username ${username}:`, error);
    throw error;
  }
};

export const fetchUserProfile = async () => {
  try {
    const response = await apiClient.get(buildApiUrl("/user/me"));
    return response.data;
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error; // Re-throw the error to handle it in the calling function
  }
};

export const createPatient = async (patientData) => {
  try {
    const requestBody = {
      patient_dob: patientData.patient_dob,
      patient_email: patientData.patient_email,
      patient_name: patientData.patient_name,
      patient_phone: patientData.patient_phone,
    };

    const response = await apiClient.post(
      buildApiUrl("/patient", API_VERSIONS.V2),
      requestBody,
    );

    return response.data;
  } catch (error) {
    console.error("Error creating patient:", error);
    throw error;
  }
};

export const fetchPatientList = async () => {
  try {
    const response = await apiClient.get(
      buildApiUrl("/patient", API_VERSIONS.V2),
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching patient list:", error);
    throw error;
  }
};

// Function to upload patient picture
export const uploadPatientPicture = async (
  patientId,
  file,
  isProfilePic = true,
) => {
  try {
    // Create FormData object
    const formData = new FormData();
    formData.append("file", file);

    // Build the URL with query parameter
    const url = buildApiUrl(`/patient/${patientId}/pic`, API_VERSIONS.V2);
    const urlWithParams = `${url}?is_profile_pic=${isProfilePic}`;

    const response = await apiClient.post(urlWithParams, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Error uploading patient picture:", error);
    throw error;
  }
};

// Alternative function with more explicit parameter handling
export const uploadPatientProfilePic = async (patientId, file) => {
  return uploadPatientPicture(patientId, file, true);
};

export const uploadPatientReferencePic = async (patientId, file) => {
  return uploadPatientPicture(patientId, file, false);
};

export const fetchPatientById = async (patientId) => {
  try {
    const response = await apiClient.get(
      buildApiUrl(`/patient/${patientId}`, API_VERSIONS.V2),
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching patient with ID ${patientId}:`, error);
    throw error;
  }
};

export const updatePatient = async (patientId, patientData) => {
  try {
    const requestBody = {
      patient_dob: patientData.patient_dob,
      patient_email: patientData.patient_email,
      patient_name: patientData.patient_name,
      patient_phone: patientData.patient_phone,
      patient_reference_pics: patientData.patient_reference_pics || [],
      patient_profile_pic: patientData.patient_profile_pic,
    };

    const response = await apiClient.put(
      buildApiUrl(`/patient/${patientId}`, API_VERSIONS.V2),
      requestBody,
    );

    return response.data;
  } catch (error) {
    console.error(`Error updating patient with ID ${patientId}:`, error);
    throw error;
  }
};

export const fetchPatientVideos = async (patientId) => {
  try {
    const response = await apiClient.get(
      buildApiUrl(`/patient/${patientId}/video`, API_VERSIONS.V2),
    );
    return response.data; // { videos: [...] }
  } catch (error) {
    console.error(`Error fetching videos for patient ${patientId}:`, error);
    throw error;
  }
};

export const uploadPatientVideo = async (
  videoFile,
  patientId,
  allowForRetraining = false,
) => {
  try {
    const formData = new FormData();
    formData.append("video", videoFile); // The field name must match the backend
    formData.append("patient_id", patientId);
    formData.append(
      "allow_for_retraining",
      allowForRetraining ? "true" : "false",
    );

    const response = await apiClient.post(
      buildApiUrl("/video", API_VERSIONS.V2),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error uploading patient video:", error);
    throw error;
  }
};

// Function to delete a patient account by username (email)
export const deletePatient = async (username) => {
  try {
    const response = await apiClient.delete(
      buildApiUrl(`/patient/${username}`, API_VERSIONS.V2),
    );
    return response.data;
  } catch (error) {
    console.error(`Error deleting user ${username}:`, error);
    throw error;
  }
};

// Function to delete a user account by username (email)
export const deleteUser = async (username) => {
  try {
    const response = await apiClient.delete(buildApiUrl(`/user/${username}`));
    return response.data;
  } catch (error) {
    console.error(`Error deleting user ${username}:`, error);
    throw error;
  }
};

export const updateVideoById = async (videoId, updateData) => {
  try {
    const payload = { ...updateData };
    if (Object.prototype.hasOwnProperty.call(payload, "allow_for_retraining")) {
      payload.allow_for_retraining = Boolean(payload.allow_for_retraining);
    }

    const response = await apiClient.patch(
      buildApiUrl(`/video/${videoId}`, API_VERSIONS.V2),
      payload,
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating video with ID ${videoId}:`, error);
    throw error;
  }
};

// Update video diagnosis with doctor_diagnosis and score
export const updateVideoDiagnosis = async (
  videoId,
  { doctor_diagnosis, score },
) => {
  try {
    const url = buildApiUrl(`/video/${videoId}/diagnosis`, API_VERSIONS.V2);
    const response = await apiClient.post(url, {
      doctor_diagnosis,
      score,
    });
    return response.data;
  } catch (error) {
    console.error("Error updating video diagnosis:", error);
    throw error;
  }
};
